import { useEffect, useRef, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import InstrumentCatalog from './components/InstrumentCatalog';
import { Link } from 'react-router-dom';

function useVisible(threshold = 0.12) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

const selectionFactors = [
  {
    icon: 'ri-graduation-cap-line',
    title: '學習階段',
    subtitle: '剛入門、穩定練習中、還是進階？',
    desc: '不同階段的學習者對音準、手感與音色的需求完全不同。不用買超過現階段需要的琴，也不需要在初學時就煩惱高階琴的選擇。',
  },
  {
    icon: 'ri-home-3-line',
    title: '生活空間',
    subtitle: '家裡的空間與擺放環境',
    desc: '古箏長度約 163cm，需要考慮家中擺放位置、溫濕度環境，以及日常練習時是否方便取用——這些都影響你適合的款式。',
  },
  {
    icon: 'ri-hand-heart-line',
    title: '手感與回饋',
    subtitle: '弦距、張力、音色偏好',
    desc: '每個人的手指條件與審美都不同。弦距太近或太遠、張力太軟或太硬，都會直接影響練習意願。親自感受過才知道。',
  },
  {
    icon: 'ri-money-cny-circle-line',
    title: '預算考量',
    subtitle: '找到預算內最值得的選擇',
    desc: '從入門練習箏到高階演奏箏，每一個價格區間都有值得的選擇。重點不是花多少，而是目前的階段最適合哪個等級。',
  },
];

const myths = [
  {
    myth: '「一定要買最貴的才學得好？」',
    truth: '不是。入門階段最重要的是音準穩定、弦距舒適，高價琴的細緻音色反而可以等熟練後，你對聲音有自己判斷時再考慮。',
  },
  {
    myth: '「一開始就要買琴才能學？」',
    truth: '可以先用課室的練習琴上課一段時間，感受古箏的聲音與手感後，再決定是否購買以及選擇什麼款式。先感受，不急。',
  },
  {
    myth: '「網路上便宜的琴可以買嗎？」',
    truth: '需要謹慎。音準、木料、弦質直接影響學習體驗，不合適的琴可能讓你以為自己學不好，其實只是琴的問題。建議先諮詢。',
  },
  {
    myth: '「古箏越大、越重就越好？」',
    truth: '不完全對。琴的品質取決於木料、工藝與共鳴設計，不是體積大小。有些過重的琴反而不好搬動，影響日常練習意願。',
  },
];

const process = [
  {
    step: '01',
    title: '先聊聊你的狀況',
    icon: 'ri-chat-3-line',
    desc: '加入 LINE@，告訴我你的學習階段、生活空間與預算範圍。不需要提前做功課，放鬆聊就好。',
    outcome: '你會得到：對自己需求更清楚的理解',
  },
  {
    step: '02',
    title: '協助評估與推薦',
    icon: 'ri-search-line',
    desc: '根據你的狀況，我會提供適合的琴款建議，說明各款的差異、優缺點，以及為什麼這個階段適合這個等級。',
    outcome: '你會得到：量身推薦的琴款與客觀比較',
  },
  {
    step: '03',
    title: '試琴與感受',
    icon: 'ri-music-line',
    desc: '有條件的話安排試琴，讓你親身感受聲音、弦距與手感。這一步很重要——琴是你要每天彈的，你的感受最重要。',
    outcome: '你會得到：實際彈奏的真實感受',
  },
  {
    step: '04',
    title: '再決定，不著急',
    icon: 'ri-heart-line',
    desc: '不強迫購買。覺得時機未到就繼續用課室琴練習，等感覺對了再決定。這個決定應該是舒服的，而不是被催促的。',
    outcome: '你會得到：一個不帶壓力的安心決定',
  },
];

export default function Instrument() {
  const hero = useVisible(0.05);
  const intro = useVisible();
  const factors = useVisible();
  const mythsSection = useVisible();
  const processSection = useVisible();
  const cta = useVisible();

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <Navbar />

      {/* ========== Hero ========== */}
      <section className="relative pt-40 pb-24 md:pt-52 md:pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://storage.helloreaddy.io/project_files/79c1f631-1cb7-456b-a6cf-fdd8ff21e9a0/a642f968-0c7e-4f61-81d5-7d770e4487d3_compressed_C--Image-202695-03_21_36.webp"
            alt=""
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#faf8f5] via-[#faf8f5]/80 to-[#faf8f5]/20"></div>
        </div>
        <div ref={hero.ref} className={`relative z-10 w-full px-6 md:px-12 lg:px-20 transition-all duration-700 ${hero.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center gap-3 mb-6">
            <div className="divider-gold"></div>
            <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-medium uppercase">Instrument</span>
          </div>

          <h1 className="font-serif-tc text-[#2c2420] leading-[1.3] mb-5" style={{ fontSize: 'clamp(2rem, 5vw, 2.75rem)' }}>
            古箏買賣<br />
            <span className="text-[#9a8470]">選箏諮詢服務</span>
          </h1>

          <p className="text-[1.1875rem] leading-[1.75] text-[#5a4e42] max-w-xl mb-7">
            提供古箏選購建議與選箏諮詢服務——依學習階段推薦、陪伴式諮詢，不強迫、不推銷。
          </p>

          {/* Service Feature Pills */}
          <div className="flex flex-wrap gap-3 mb-8">
            {[
              { icon: 'ri-chat-smile-3-line', label: '先加入 LINE 諮詢，不急著買' },
              { icon: 'ri-filter-3-line', label: '依學習階段與預算精準推薦' },
              { icon: 'ri-shield-check-line', label: '先確認適合再決定，不強迫' },
            ].map((pill, i) => (
              <div
                key={i}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/85 backdrop-blur-sm border border-[#e8ddd0]/60 text-[0.875rem] text-[#4a3e32] font-medium"
              >
                <i className={`${pill.icon} text-[#7a6248]`}></i>
                {pill.label}
              </div>
            ))}
          </div>

          {/* Hero CTA */}
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="https://line.me"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-[#5a3e22] hover:bg-[#3a2e22] text-[#f5f0e8] text-[0.9375rem] font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-line-fill text-[#06C755] text-lg"></i>
              加入 LINE@ 諮詢
            </a>
            <a
              href="#catalog"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 border-[#7a6248] text-[#7a6248] text-[0.9375rem] font-semibold hover:bg-[#7a6248] hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              直接看產品與價格
              <i className="ri-arrow-down-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* ========== Intro / Trust Section ========== */}
      <section className="py-24 md:py-32 bg-[#faf8f5]">
        <div className="w-full px-6 md:px-12 lg:px-20">
          <div ref={intro.ref} className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center transition-all duration-700 ${intro.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {/* Left: Text */}
            <div>
              <div className="flex items-center gap-3 mb-7">
                <div className="divider-gold"></div>
                <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-medium uppercase">Our Approach</span>
              </div>
              <h2 className="font-serif-tc text-[#2c2420] leading-[1.4] mb-7" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}>
                信任延伸，<br />不是商業推銷
              </h2>
              <p className="text-[1.1875rem] leading-[1.75] text-[#5a4e42] mb-8">
                很多人在開始學琴前會問：「我需要先買一把古箏嗎？」我的答案是——先感受，再決定。我不會在你第一堂課就催促你購買。等你對古箏有了一定的感受與理解，我們再一起評估是否需要、適合什麼款式。
              </p>

              {/* Three Principles */}
              <div className="space-y-5 mb-8">
                {[
                  {
                    num: '01',
                    title: '不急著買',
                    desc: '先上課、先感受。等你對古箏的聲音與手感有了自己的體會，再來討論是否需要買琴。這樣買到的琴，才是你真正想要的。',
                  },
                  {
                    num: '02',
                    title: '先看需求',
                    desc: '每個人的學習階段、生活空間、手感偏好都不同。我不會直接推薦「最好的」，而是幫你釐清「最適合你現在狀況的」。',
                  },
                  {
                    num: '03',
                    title: '依預算與學習階段建議',
                    desc: '從入門到進階，每個價格區間都有值得的選擇。我會根據你的階段與預算，說明各等級的差異，讓你做一個清楚且有把握的決定。',
                  },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-5">
                    <span className="text-[#c8b89a] font-bold text-[1.5rem] leading-none shrink-0 mt-0.5" style={{ fontFamily: 'var(--font-heading)' }}>
                      {item.num}
                    </span>
                    <div>
                      <h4 className="font-serif-tc text-[#3a2e22] font-semibold mb-1.5" style={{ fontSize: '1.0625rem' }}>{item.title}</h4>
                      <p className="text-[0.9375rem] leading-[1.7] text-[#7a6e62]">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-[1rem] leading-[1.75] text-[#5a4e42]">
                選箏諮詢服務是鳴箏品牌對學員的延伸陪伴，幫助你在學習路上做出最適合自己的選擇。
              </p>
            </div>

            {/* Right: Image */}
            <div className="rounded-2xl overflow-hidden" style={{ aspectRatio: '4/3' }}>
              <img
                src="https://storage.helloreaddy.io/project_files/79c1f631-1cb7-456b-a6cf-fdd8ff21e9a0/9ecb77f7-c14b-4e91-ba8c-ed30a83f21a5_compressed_f2546f62-27ce-4ab6-93b2-df43abd32a07.webp"
                alt="選箏諮詢"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ========== Selection Factors ========== */}
      <section className="py-24 md:py-32 bg-[#f2ede5]">
        <div className="w-full px-6 md:px-12 lg:px-20">
          <div ref={factors.ref} className={`transition-all duration-700 ${factors.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="text-center mb-14">
              <div className="flex items-center justify-center gap-3 mb-5">
                <div className="divider-gold"></div>
                <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-medium uppercase">Selection Criteria</span>
                <div className="divider-gold"></div>
              </div>
              <h2 className="font-serif-tc text-[#2c2420] leading-[1.4] mb-4" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}>
                選琴會考慮的四個面向
              </h2>
              <p className="text-[1.1875rem] leading-[1.75] text-[#7a6e62] max-w-lg mx-auto">
                沒有一把琴是「最好的」，只有最適合你現在狀況的。這四個面向幫你聚焦思考。
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {selectionFactors.map((f, i) => (
                <div
                  key={i}
                  className="bg-[#faf8f5] rounded-2xl p-7 transition-all duration-700 hover:translate-y-[-4px]"
                  style={{ transitionDelay: `${i * 0.1}s` }}
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#ede8e0] mb-5">
                    <i className={`${f.icon} text-[#7a6248] text-xl`}></i>
                  </div>
                  <h3 className="font-serif-tc text-[#2c2420] font-semibold mb-2" style={{ fontSize: '1.125rem' }}>{f.title}</h3>
                  <p className="text-[0.875rem] text-[#9a8470] mb-3 font-medium">{f.subtitle}</p>
                  <p className="text-[0.9375rem] leading-[1.7] text-[#7a6e62]">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== Product Catalog (Core) ========== */}
      <InstrumentCatalog />

      {/* ========== Myths ========== */}
      <section className="py-24 md:py-32 bg-[#faf8f5]">
        <div className="w-full px-6 md:px-12 lg:px-20">
          <div ref={mythsSection.ref} className={`transition-all duration-700 ${mythsSection.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="text-center mb-14">
              <div className="flex items-center justify-center gap-3 mb-5">
                <div className="divider-gold"></div>
                <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-medium uppercase">Common Myths</span>
                <div className="divider-gold"></div>
              </div>
              <h2 className="font-serif-tc text-[#2c2420] leading-[1.4] mb-4" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}>
                選琴常見迷思破解
              </h2>
              <p className="text-[1.1875rem] leading-[1.75] text-[#7a6e62] max-w-lg mx-auto">
                這些是學員最常問的問題。選琴不需要焦慮，先弄清楚觀念，決定就會更安心。
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto">
              {myths.map((m, i) => (
                <div
                  key={i}
                  className="bg-[#f5f0e8] rounded-2xl p-6 transition-all duration-700 hover:translate-y-[-3px]"
                  style={{ transitionDelay: `${i * 0.08}s` }}
                >
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#d6c8b0]/40 shrink-0 mt-0.5">
                      <i className="ri-question-line text-[#9a8470] text-sm"></i>
                    </div>
                    <p className="font-semibold text-[#4a3e32] text-[1.0625rem] leading-[1.6]">{m.myth}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#4a6741]/15 shrink-0 mt-0.5">
                      <i className="ri-check-line text-[#4a6741] text-sm"></i>
                    </div>
                    <p className="text-[0.9375rem] leading-[1.75] text-[#5a4e42]">{m.truth}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== Process ========== */}
      <section className="py-24 md:py-32 bg-[#f2ede5]">
        <div className="w-full px-6 md:px-12 lg:px-20">
          <div ref={processSection.ref} className={`transition-all duration-700 ${processSection.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="text-center mb-14">
              <div className="flex items-center justify-center gap-3 mb-5">
                <div className="divider-gold"></div>
                <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-medium uppercase">How It Works</span>
                <div className="divider-gold"></div>
              </div>
              <h2 className="font-serif-tc text-[#2c2420] leading-[1.4] mb-4" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}>
                選琴流程
              </h2>
              <p className="text-[1.1875rem] leading-[1.75] text-[#7a6e62] max-w-lg mx-auto">
                從初步諮詢到最終決定，每一步都讓你安心、清楚、不帶壓力。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {process.map((p, i) => (
                <div
                  key={i}
                  className="relative bg-[#faf8f5] rounded-2xl p-7 transition-all duration-700 hover:translate-y-[-3px]"
                  style={{ transitionDelay: `${i * 0.1}s` }}
                >
                  {/* Step Number */}
                  <span className="text-[3.5rem] font-extrabold text-[#7a6248]/10 leading-none select-none block mb-3">
                    {p.step}
                  </span>

                  {/* Icon + Title */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#ede8e0] shrink-0">
                      <i className={`${p.icon} text-[#7a6248] text-base`}></i>
                    </div>
                    <h3 className="font-serif-tc text-[#2c2420] font-semibold" style={{ fontSize: '1.0625rem' }}>{p.title}</h3>
                  </div>

                  {/* Description */}
                  <p className="text-[0.9375rem] leading-[1.7] text-[#5a4e42] mb-4">{p.desc}</p>

                  {/* Outcome */}
                  <div className="pt-4 border-t border-[#ede5d8]">
                    <p className="text-[0.8125rem] text-[#9a8470] leading-[1.5] font-medium">{p.outcome}</p>
                  </div>

                  {/* Arrow connector */}
                  {i < process.length - 1 && (
                    <div className="hidden lg:block absolute -right-4 top-1/2 -translate-y-1/2 z-10">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#ede8e0]">
                        <i className="ri-arrow-right-s-line text-[#b08060] text-lg"></i>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== CTA ========== */}
      <section className="py-24 md:py-32 bg-[#faf8f5]">
        <div ref={cta.ref} className={`w-full px-6 md:px-12 lg:px-20 text-center transition-all duration-700 ${cta.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="divider-gold"></div>
            <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-medium uppercase">Next Step</span>
            <div className="divider-gold"></div>
          </div>

          <h2 className="font-serif-tc text-[#2c2420] leading-[1.35] mb-5" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}>
            想了解更多選琴建議？
          </h2>

          <p className="text-[1.1875rem] leading-[1.75] text-[#5a4e42] max-w-xl mx-auto mb-3">
            先加入 LINE@ 聊聊你的學習狀況，我會根據你的實際需求給出最適合的選琴建議。
          </p>
          <p className="text-[0.9375rem] leading-[1.75] text-[#9a8470] max-w-lg mx-auto mb-10">
            不急著購買，先聊聊也可以。確認適不適合，再決定是否需要。
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="https://line.me"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full bg-[#5a3e22] text-[#f5f0e8] text-[1rem] font-semibold hover:bg-[#3a2e22] transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-line-fill text-[#06C755] text-lg"></i>
              加入 LINE@ 諮詢
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border-2 border-[#7a6248] text-[#7a6248] text-[1rem] font-semibold hover:bg-[#7a6248] hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-line"></i>
              前往聯絡頁
            </Link>
          </div>

          <p className="mt-8 text-[0.875rem] text-[#b0a090]">
            不需要任何準備，帶著你的好奇與問題就好。
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}